import { User } from '../../models/User'
import { env } from '../../common/util'

export enum UserConsentState {
  REFUSED = 'refused',
  ACCEPTED = 'accepted',
  REVOKED = 'revoked',
  UNKNOWN = 'unknown',
}

export type UserConsent = {
  current: {
    context: string[] | string | null
    state: UserConsentState
    room_id: string
    session_id: string
    permissionAsked?: boolean
    discussed?: boolean
  }
}

export type ConsentResult = {
  context: string[] | string | null
  discussed: boolean
  consentGained: boolean
  permissionAsked: boolean
}

export type ConsentQuote = {
  speaker: string | null
  text: string
}

const QUOTE_SEPARATOR = '§'
const SPEAKER_PREFIX = /^([^:§"]{1,80}):\s*(.*)$/s

const unquote = (line: string) =>
  line
    .trim()
    .replace(/^["'“]+|["'”]+$/g, '')
    .trim()

export const splitConsentReason = (reason: string): string[] => {
  const trimmed = reason.trim()
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed: unknown = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return parsed
          .map(String)
          .map((line) => line.trim())
          .filter(Boolean)
      }
    } catch {
      return trimmed.slice(1, -1).split(',').map(unquote).filter(Boolean)
    }
  }
  return trimmed
    .split(QUOTE_SEPARATOR)
    .map((line) => line.trim())
    .filter(Boolean)
}

export const parseConsentQuote = (line: string): ConsentQuote => {
  const match = SPEAKER_PREFIX.exec(line)
  if (!match) {
    return { speaker: null, text: line }
  }
  return { speaker: match[1].trim(), text: match[2].trim() }
}

const calculateCurrentState = (result: ConsentResult, currentState: UserConsentState) => {
  if (result.consentGained) {
    return UserConsentState.ACCEPTED
  } else if (currentState === UserConsentState.ACCEPTED || currentState === UserConsentState.REVOKED) {
    return UserConsentState.REVOKED
  } else if (result.consentGained === null) {
    return UserConsentState.UNKNOWN
  }
  return UserConsentState.REFUSED
}

export const mapToUserConsent = (result: ConsentResult, user: User, roomId?: string, sessionId?: string): UserConsent => {
  const userConsent = getUserMetadataConsent(user)

  return {
    current: {
      discussed: result.discussed,
      permissionAsked: result.permissionAsked,
      context: result.context ?? userConsent?.current?.context ?? null,
      state:
        result && (result.discussed || result.permissionAsked)
          ? calculateCurrentState(result, userConsent?.current?.state ?? UserConsentState.UNKNOWN)
          : UserConsentState.UNKNOWN,
      room_id: roomId ?? userConsent?.current?.room_id ?? '',
      session_id: sessionId ?? userConsent?.current?.session_id ?? '',
    },
  }
}

const CONSENT_TRACKING_PRESET_MAP: { [key: string]: string } = {
  dev: 'cwboiwmhv9igg340',
  staging: 'cvr7c4k4serlhg87',
  prod: '8qhq03dfdtg9ygcu',
}

export const CONSENT_TRACKING_PRESET_ID = CONSENT_TRACKING_PRESET_MAP[env]

export const parseUserMetadata = (user: User) => {
  if (!user.metadata) {
    return {}
  }
  try {
    return JSON.parse(user.metadata)
  } catch {
    console.debug(`Could not parse user metadata: ${user.metadata}`)
  }
  return {}
}

export const getUserMetadataConsent = (user: User): UserConsent | null => {
  const userMetadata = parseUserMetadata(user)
  return ('zocksConsent' in userMetadata && userMetadata['zocksConsent']) || null
}

export const resolveUserConsent = (user: User, consent?: UserConsent | null): UserConsent | null => {
  return consent ?? getUserMetadataConsent(user)
}

export const MEETING_NOT_AVAILABLE_LABEL = 'Meeting not available'
